import {
  ContributorsScheduleQuery,
  ProjectPlannedTimeFragment,
  ProjectScheduleEmployeeFragment,
  ProjectScheduleProjectWorkerFragment,
} from 'generated/resources/graphql'
import { EmployeeForSelectFragment } from 'generated/resources/graphql'
import moment, { Moment } from 'moment'

import i18n from 'modules/core/lib/i18n'

import { contactFullName } from 'modules/finance/lib/contactsUtils'
import { ScheduleParseDataType } from 'modules/project_management/components/Schedule/Body'
import { ScheduleRecordType } from 'modules/project_management/components/Schedule/Body/ScheduleItem/types'

export const parseData = (
  data: ContributorsScheduleQuery | undefined,
  startDate: Moment,
  endDate: Moment
): ScheduleParseDataType => {
  const employeeToSelectFragment = (
    employee: ProjectScheduleEmployeeFragment
  ): EmployeeForSelectFragment => {
    return {
      id: employee.id,
      fullName: employee.fullName,
      firstName: employee.firstName,
      lastName: employee.lastName,
      access: { id: employee.id, avatar: null },
    }
  }

  const transformRecords = (
    projectWorker: ProjectScheduleProjectWorkerFragment,
    employee: EmployeeForSelectFragment
  ): ScheduleRecordType => {
    return projectWorker.plannedRecords.reduce<ScheduleRecordType>((acc, record) => {
      const key = record.subproject?.id ? record.subproject?.id?.toString() : 'null'

      if (!acc[key]) {
        acc[key] = []
      }

      if (
        projectWorker.project &&
        moment(record.startDate).isSameOrBefore(endDate) &&
        moment(record.endDate).isSameOrAfter(startDate)
      ) {
        acc[key].push({
          id: record.id,
          startDate: record.startDate,
          endDate: record.endDate,
          realStartDate: record.startDate,
          realEndDate: record.endDate,
          dailyMinutes: record.dailyMinutes,
          group: projectWorker.project?.name ?? '',
          title: record.subproject ? record.subproject.name : null,
          subtitle: contactFullName(projectWorker.project?.client) || null,
          projectWorkerId: projectWorker.id,
          project: projectWorker.project ?? undefined,
          canEditAssignments: projectWorker.project?._policies?.assignEditor?.can ?? false,
          subproject: record.subproject ?? undefined,
          employee: employee,
          weekDays: record.weekDays,
        })
      }

      if (acc[key].length === 0) {
        delete acc[key]
      }

      return acc
    }, {})
  }

  const generateRows = (
    projectWorkers: ProjectScheduleProjectWorkerFragment[],
    employeeToSelect: EmployeeForSelectFragment
  ) => {
    return projectWorkers
      .map((pw: ProjectScheduleProjectWorkerFragment) => {
        const records = transformRecords(pw, employeeToSelect)
        if (Object.keys(records).length === 0) return null

        return {
          firstColumn: {
            id: pw.project?.id.toString() || '',
            title: pw.project?.name || '',
            subtitle: contactFullName(pw.project?.client) || '',
          },
          records: records,
        }
      })
      .filter((row) => row !== null)
  }

  const generatePlannedTimes = (plannedTimes: ProjectPlannedTimeFragment[]) => {
    if (plannedTimes.length === 0) return []

    const getDatesArray = () => {
      const dates = []
      const current = moment(startDate)

      while (current.isSameOrBefore(endDate, 'day')) {
        dates.push(current.clone())
        current.add(1, 'day')
      }

      return dates
    }

    const dateRange = getDatesArray()

    const totalByDays = dateRange.map((day) => {
      return plannedTimes.reduce((acc, plannedTime) => {
        if (
          moment(plannedTime.startDate).isSameOrBefore(day, 'day') &&
          moment(plannedTime.endDate).isSameOrAfter(day, 'day') &&
          plannedTime.weekDays.includes(day.weekday())
        ) {
          return acc + plannedTime.dailyMinutes
        }
        return acc
      }, 0)
    })

    const groupConsecutive = (arr: number[]) => {
      const result = []
      let current = { total: arr[0] || 0, count: 1 }

      for (let i = 1; i < arr.length; i++) {
        if (arr[i] === current.total) {
          current.count++
        } else {
          result.push(current)
          current = { total: arr[i] || 0, count: 1 }
        }
      }

      result.push(current)
      return result
    }

    const groupedTotalByDays = groupConsecutive(totalByDays)

    const generateBlocksFromGroups = (
      arrGroups: { total: number; count: number }[]
    ): ScheduleRecordType => {
      const blocks = []
      const current = moment(startDate)

      for (const group of arrGroups) {
        const { total, count } = group

        if (total !== 0) {
          const blockStart = current.clone()
          const blockEnd = current.clone().add(count - 1, 'days')

          blocks.push({
            id: undefined,
            group: i18n.t('project_management.schedule.other_assignments.title'),
            title: null,
            subtitle: null,
            projectWorkerId: undefined,
            project: undefined,
            employee: undefined,
            startDate: blockStart.format('YYYY-MM-DD'),
            endDate: blockEnd.format('YYYY-MM-DD'),
            realStartDate: blockStart.format('YYYY-MM-DD'),
            realEndDate: blockEnd.format('YYYY-MM-DD'),
            dailyMinutes: total,
          })
        }

        current.add(count, 'days')
      }

      return { hidden: blocks }
    }

    return [
      {
        firstColumn: {
          id: '',
          title: i18n.t('project_management.schedule.other_assignments.title'),
          subtitle: '',
        },
        records: generateBlocksFromGroups(groupedTotalByDays),
        otherAssignments: true,
      },
    ]
  }

  return {
    items:
      data?.projectManagement.projectContributorsConnection.edges
        .filter((edge) => edge.node.employee)
        .flatMap(({ node: contributor }) => {
          if (!contributor.employee) return []

          const employee: ProjectScheduleEmployeeFragment = contributor.employee

          const employeeToSelect = employeeToSelectFragment(employee)

          const estimatedWorkTimesData = contributor.estimatedWorkTimes.reduce(
            (acc, estimatedWorkTime) => {
              const expectedMinutes = Math.max(estimatedWorkTime.expectedMinutes, 0)
              const minutes = estimatedWorkTime.minutes
              const hasDayOff = estimatedWorkTime.offDays.some((offDay) => !offDay.workable)
              const minutesOff = hasDayOff ? Math.max(expectedMinutes - minutes, 0) : 0

              return {
                ...acc,
                [estimatedWorkTime.date]: {
                  expectedMinutes: expectedMinutes,
                  minutes: minutes,
                  minutesOff: minutesOff,
                },
              }
            },
            {}
          )

          return {
            headerRow: {
              id: employee.id.toString() || '0',
              fullName: employee.fullName || '',
              avatar: employee.avatar?.url || '',
              jobTitle: employee.referenceContract?.jobTitle || '',
              estimatedWorkTimesData: estimatedWorkTimesData,
            },
            employeeToSelect: employeeToSelect,
            rows: generateRows(contributor.projectWorkers, employeeToSelect).concat(
              generatePlannedTimes(contributor.plannedTimes)
            ),
          }
        }) || [],
    totalCount: data?.projectManagement.projectContributorsConnection.totalCount || 0,
  }
}
